import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  HelpCircle, X, Lightbulb, Eye, ChevronRight, 
  Calendar, MapPin, Users, DollarSign, Car, ShoppingBag,
  Clock, CheckCircle2, CreditCard, Image as ImageIcon
} from 'lucide-react';

// Help content for each screen
const SCREEN_HELP_CONTENT = {
  itinerary: {
    title: 'Roteiro da Viagem',
    icon: '📅',
    description: 'Organize cada momento da sua viagem com um roteiro detalhado.',
    features: [
      { icon: Calendar, text: 'Adicione atividades por dia e horário' },
      { icon: MapPin, text: 'Defina locais e endereços (clicáveis no mapa)' },
      { icon: DollarSign, text: 'Registre custos e gere pagamentos automaticamente' },
      { icon: Users, text: 'Atribua atividades a membros específicos' },
      { icon: ImageIcon, text: 'Adicione fotos de cada atividade' },
    ],
    example: {
      title: 'Exemplo: Viagem para Gramado',
      items: [
        {
          day: 'Sexta - 15/03',
          activities: [
            { time: '08:00', title: 'Saída de Porto Alegre', location: 'Ponto de encontro: Parcão', cost: null, category: '🚗 Transporte' },
            { time: '11:30', title: 'Check-in Hotel Serra Azul', location: 'Av. Borges de Medeiros, 2500', cost: 'R$ 450/pessoa', category: '🏨 Hospedagem' },
            { time: '13:00', title: 'Almoço no Lago Negro', location: 'Restaurante Bela Vista', cost: 'R$ 85/pessoa', category: '🍽️ Alimentação' },
            { time: '15:00', title: 'Mini Mundo', location: 'Rua Horácio Cardoso, 291', cost: 'R$ 60/pessoa', category: '🎯 Passeio' },
          ]
        },
        {
          day: 'Sábado - 16/03',
          activities: [
            { time: '09:00', title: 'Café da manhã no hotel', location: 'Hotel Serra Azul', cost: 'Incluso', category: '🍽️ Alimentação' },
            { time: '10:30', title: 'Snowland', location: 'RS-235, Km 31', cost: 'R$ 189/pessoa', category: '🎯 Passeio' },
            { time: '19:00', title: 'Jantar fondue', location: 'Colosseo Restaurante', cost: 'R$ 150/pessoa', category: '🍽️ Alimentação' },
          ]
        }
      ]
    }
  },
  
  payments: {
    title: 'Pagamentos',
    icon: '💰',
    description: 'Controle todas as despesas e veja quem já pagou.',
    features: [
      { icon: DollarSign, text: 'Crie despesas compartilhadas entre o grupo' },
      { icon: Users, text: 'Divisão automática entre participantes' },
      { icon: CheckCircle2, text: 'Acompanhe quem já pagou' },
      { icon: CreditCard, text: 'Vincule pagamentos a itens do roteiro' },
      { icon: ImageIcon, text: 'Anexe comprovantes de pagamento' },
    ],
    example: {
      title: 'Exemplo de Despesas',
      items: [
        { title: 'Hotel Serra Azul (3 noites)', total: 'R$ 2.250,00', perPerson: 'R$ 450,00', paid: 4, pending: 1 },
        { title: 'Ingressos Snowland', total: 'R$ 945,00', perPerson: 'R$ 189,00', paid: 5, pending: 0 },
        { title: 'Combustível ida/volta', total: 'R$ 320,00', perPerson: 'R$ 64,00', paid: 3, pending: 2 },
        { title: 'Jantar fondue (Sábado)', total: 'R$ 750,00', perPerson: 'R$ 150,00', paid: 2, pending: 3 },
      ]
    }
  },
  
  members: {
    title: 'Participantes',
    icon: '👥',
    description: 'Gerencie quem participa da viagem.',
    features: [
      { icon: Users, text: 'Veja todos os membros confirmados' },
      { icon: CheckCircle2, text: 'Aprove ou rejeite solicitações de entrada' },
      { icon: Clock, text: 'Controle quem está pendente de confirmação' },
      { icon: Eye, text: 'Veja detalhes de cada participante' },
    ],
    example: {
      title: 'Exemplo de Grupo',
      items: [
        { name: 'Maria Silva', role: 'Organizadora', status: 'Confirmado', emoji: '👩' },
        { name: 'João Santos', role: 'Viajante', status: 'Confirmado', emoji: '👨' },
        { name: 'Ana Costa', role: 'Viajante', status: 'Confirmado', emoji: '👩' },
        { name: 'Pedro Lima', role: 'Viajante', status: 'Pendente', emoji: '👨' },
        { name: 'Carla Dias', role: 'Viajante', status: 'Confirmado', emoji: '👩' },
      ]
    }
  },
  
  carpools: {
    title: 'Caronas',
    icon: '🚗',
    description: 'Organize o transporte do grupo.',
    features: [
      { icon: Car, text: 'Ofereça ou peça carona' },
      { icon: MapPin, text: 'Defina pontos de encontro' },
      { icon: Users, text: 'Veja vagas disponíveis' },
      { icon: DollarSign, text: 'Divida custos de combustível' },
    ],
    example: {
      title: 'Exemplo de Caronas',
      items: [
        { driver: 'Maria', car: 'Honda Civic Preto', seats: '4 lugares', route: 'Porto Alegre → Gramado', passengers: ['João', 'Ana'], available: 2, cost: 'R$ 64/pessoa' },
        { driver: 'Pedro', car: 'VW Gol Prata', seats: '4 lugares', route: 'Canoas → Gramado', passengers: ['Carla'], available: 3, cost: 'R$ 50/pessoa' },
      ]
    }
  },
  
  purchases: {
    title: 'Compras',
    icon: '🛒',
    description: 'Liste itens que o grupo precisa comprar.',
    features: [
      { icon: ShoppingBag, text: 'Crie listas de compras compartilhadas' },
      { icon: Users, text: 'Atribua responsáveis por cada item' },
      { icon: CheckCircle2, text: 'Marque itens como comprados' },
      { icon: DollarSign, text: 'Registre valores e divida custos' },
    ],
    example: {
      title: 'Exemplo: Lista para Churrasco',
      items: [
        { item: 'Carne (5kg picanha)', responsible: 'João', status: 'Comprado', cost: 'R$ 250,00' },
        { item: 'Carvão (2 sacos)', responsible: 'Pedro', status: 'Comprado', cost: 'R$ 60,00' },
        { item: 'Bebidas', responsible: 'Ana', status: 'Pendente', cost: '~R$ 150,00' },
        { item: 'Pão de alho', responsible: 'Maria', status: 'Pendente', cost: '~R$ 30,00' },
        { item: 'Gelo', responsible: 'Carla', status: 'Pendente', cost: '~R$ 20,00' },
      ]
    }
  },
  
  gallery: {
    title: 'Galeria',
    icon: '📸',
    description: 'Compartilhe fotos da viagem.',
    features: [
      { icon: ImageIcon, text: 'Faça upload de fotos da viagem' },
      { icon: Calendar, text: 'Fotos organizadas por dia/atividade' },
      { icon: Users, text: 'Todos os membros podem contribuir' },
    ],
    example: {
      title: 'Exemplo de Galeria',
      description: 'Organize as fotos por momentos: chegada, passeios, refeições, etc. Todos os participantes podem enviar fotos!'
    }
  },
  
  checklist: {
    title: 'Checklist',
    icon: '✅',
    description: 'Lista de preparativos antes da viagem.',
    features: [
      { icon: CheckCircle2, text: 'Crie checklists personalizados' },
      { icon: Users, text: 'Atribua tarefas a membros' },
      { icon: Clock, text: 'Defina prazos' },
    ],
    example: {
      title: 'Exemplo: Preparativos',
      items: [
        { task: 'Confirmar reserva do hotel', done: true },
        { task: 'Comprar ingressos antecipados', done: true },
        { task: 'Revisar carro (óleo, pneus)', done: false },
        { task: 'Separar documentos', done: false },
        { task: 'Carregar power bank', done: false },
      ]
    }
  },

  // Main trip overview
  overview: {
    title: 'Visão Geral da Viagem',
    icon: '✈️',
    description: 'Veja como organizar uma viagem completa no ViajeJunto.',
    features: [
      { icon: Calendar, text: 'Monte o roteiro dia a dia' },
      { icon: Users, text: 'Convide amigos pelo código' },
      { icon: DollarSign, text: 'Controle gastos compartilhados' },
      { icon: Car, text: 'Organize caronas' },
      { icon: ShoppingBag, text: 'Faça listas de compras' },
      { icon: ImageIcon, text: 'Compartilhe fotos' },
    ],
    fullExample: {
      title: '🏖️ Exemplo Completo: Fim de Semana em Paraty',
      trip: {
        name: 'Fim de Semana em Paraty',
        destination: 'Paraty, RJ',
        dates: '22-24 de Março, 2024',
        members: 6,
        budget: 'R$ 800/pessoa',
        status: 'Confirmada'
      },
      sections: [
        {
          title: '📅 Roteiro',
          content: [
            'Sexta 18h: Saída do Rio (2 carros)',
            'Sexta 22h: Check-in Pousada + Jantar',
            'Sábado: Centro histórico + Praias',
            'Sábado noite: Cachaçaria + Jantar',
            'Domingo: Praia do Sono + Retorno'
          ]
        },
        {
          title: '💰 Despesas Previstas',
          content: [
            'Pousada 2 noites: R$ 1.800 (R$ 300/pessoa)',
            'Combustível: R$ 600 (R$ 100/pessoa)',
            'Passeio de barco: R$ 480 (R$ 80/pessoa)',
            'Alimentação: ~R$ 300/pessoa',
            'Extras: ~R$ 120/pessoa'
          ]
        },
        {
          title: '🚗 Caronas',
          content: [
            'Carro 1 (João): 4 vagas - Saindo da Tijuca',
            'Carro 2 (Maria): 4 vagas - Saindo da Barra',
            'Custo dividido por passageiro'
          ]
        },
        {
          title: '🛒 Lista de Compras',
          content: [
            'Cooler com gelo',
            'Bebidas para viagem',
            'Snacks',
            'Protetor solar',
            'Repelente'
          ]
        }
      ]
    }
  }
};

export const ScreenHelp = ({ screen = 'overview', className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('features');
  
  const content = SCREEN_HELP_CONTENT[screen] || SCREEN_HELP_CONTENT.overview;

  return (
    <>
      {/* Help Button */}
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        className={`rounded-full gap-2 text-slate-600 dark:text-slate-400 hover:text-orange-600 hover:border-orange-300 ${className}`}
        data-testid={`help-btn-${screen}`}
      >
        <HelpCircle className="w-4 h-4" />
        <span>Ajuda</span>
      </Button>

      {/* Help Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-lg max-h-[85vh] overflow-hidden"
            >
              <Card className="border-0 shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{content.icon}</span>
                      <div>
                        <h2 className="font-bold text-lg">{content.title}</h2>
                        <p className="text-white/80 text-sm">{content.description}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsOpen(false)}
                      className="text-white hover:bg-white/20 h-8 w-8"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 p-1 m-2 mb-0" style={{width: 'calc(100% - 16px)'}}>
                    <TabsTrigger value="features" className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      O que fazer
                    </TabsTrigger>
                    <TabsTrigger value="example" className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Exemplo
                    </TabsTrigger>
                  </TabsList>

                  <div className="overflow-y-auto max-h-[50vh]">
                    {/* Features Tab */}
                    <TabsContent value="features" className="p-4 pt-2 m-0">
                      <div className="space-y-3">
                        {content.features?.map((feature, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                              <feature.icon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                            </div>
                            <p className="text-sm text-slate-700 dark:text-slate-300 pt-1">{feature.text}</p>
                          </div>
                        ))}
                      </div>
                    </TabsContent>

                    {/* Example Tab */}
                    <TabsContent value="example" className="p-4 pt-2 m-0">
                      {content.fullExample ? (
                        <FullTripExample example={content.fullExample} />
                      ) : content.example ? (
                        <ExampleContent screen={screen} example={content.example} />
                      ) : (
                        <p className="text-slate-500 text-center py-8">Exemplo em breve...</p>
                      )}
                    </TabsContent>
                  </div>
                </Tabs>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 dark:border-slate-700">
                  <Button 
                    className="w-full rounded-full bg-orange-500 hover:bg-orange-600"
                    onClick={() => setIsOpen(false)}
                  >
                    Entendi!
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Component to render different example types
const ExampleContent = ({ screen, example }) => {
  switch (screen) {
    case 'itinerary':
      return <ItineraryExample example={example} />;
    case 'payments':
      return <PaymentsExample example={example} />;
    case 'members':
      return <MembersExample example={example} />;
    case 'carpools':
      return <CarpoolsExample example={example} />;
    case 'purchases':
      return <PurchasesExample example={example} />;
    case 'checklist':
      return <ChecklistExample example={example} />;
    default:
      return <GenericExample example={example} />;
  }
};

const ItineraryExample = ({ example }) => (
  <div className="space-y-4">
    <h4 className="font-semibold text-slate-900 dark:text-white">{example.title}</h4>
    {example.items.map((day, i) => (
      <div key={i} className="space-y-2">
        <Badge variant="outline" className="bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
          {day.day}
        </Badge>
        <div className="space-y-2 ml-2">
          {day.activities.map((act, j) => (
            <div key={j} className="flex items-start gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
              <span className="font-mono text-orange-600 dark:text-orange-400 w-12 flex-shrink-0">{act.time}</span>
              <div className="flex-1">
                <p className="font-medium text-slate-900 dark:text-white">{act.title}</p>
                <p className="text-slate-500 dark:text-slate-400 text-xs">{act.location}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-[10px]">{act.category}</Badge>
                  {act.cost && <span className="text-xs text-green-600 dark:text-green-400">{act.cost}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const PaymentsExample = ({ example }) => (
  <div className="space-y-3">
    <h4 className="font-semibold text-slate-900 dark:text-white">{example.title}</h4>
    {example.items.map((item, i) => (
      <div key={i} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <div className="flex justify-between items-start mb-2">
          <p className="font-medium text-slate-900 dark:text-white text-sm">{item.title}</p>
          <span className="font-bold text-orange-600">{item.total}</span>
        </div>
        <div className="flex justify-between items-center text-xs">
          <span className="text-slate-500">{item.perPerson}/pessoa</span>
          <div className="flex items-center gap-2">
            <span className="text-green-600">✓ {item.paid} pagos</span>
            {item.pending > 0 && <span className="text-amber-600">⏳ {item.pending} pendentes</span>}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const MembersExample = ({ example }) => (
  <div className="space-y-3">
    <h4 className="font-semibold text-slate-900 dark:text-white">{example.title}</h4>
    {example.items.map((member, i) => (
      <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <span className="text-2xl">{member.emoji}</span>
        <div className="flex-1">
          <p className="font-medium text-slate-900 dark:text-white text-sm">{member.name}</p>
          <p className="text-xs text-slate-500">{member.role}</p>
        </div>
        <Badge className={member.status === 'Confirmado' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
          {member.status}
        </Badge>
      </div>
    ))}
  </div>
);

const CarpoolsExample = ({ example }) => (
  <div className="space-y-3">
    <h4 className="font-semibold text-slate-900 dark:text-white">{example.title}</h4>
    {example.items.map((carpool, i) => (
      <div key={i} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">🚗</span>
            <div>
              <p className="font-medium text-slate-900 dark:text-white text-sm">{carpool.driver}</p>
              <p className="text-xs text-slate-500">{carpool.car}</p>
            </div>
          </div>
          <Badge variant="outline">{carpool.available} vagas</Badge>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
          <MapPin className="w-3 h-3" />
          {carpool.route}
        </div>
        <div className="flex items-center justify-between text-xs">
          <span>Passageiros: {carpool.passengers.join(', ')}</span>
          <span className="text-green-600 font-medium">{carpool.cost}</span>
        </div>
      </div>
    ))}
  </div>
);

const PurchasesExample = ({ example }) => (
  <div className="space-y-3">
    <h4 className="font-semibold text-slate-900 dark:text-white">{example.title}</h4>
    {example.items.map((item, i) => (
      <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <span className={`text-lg ${item.status === 'Comprado' ? '' : 'opacity-50'}`}>
          {item.status === 'Comprado' ? '✅' : '⬜'}
        </span>
        <div className="flex-1">
          <p className={`font-medium text-sm ${item.status === 'Comprado' ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
            {item.item}
          </p>
          <p className="text-xs text-slate-500">Responsável: {item.responsible}</p>
        </div>
        <span className="text-xs text-slate-600">{item.cost}</span>
      </div>
    ))}
  </div>
);

const ChecklistExample = ({ example }) => (
  <div className="space-y-3">
    <h4 className="font-semibold text-slate-900 dark:text-white">{example.title}</h4>
    {example.items.map((item, i) => (
      <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <span className="text-lg">{item.done ? '✅' : '⬜'}</span>
        <p className={`text-sm ${item.done ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
          {item.task}
        </p>
      </div>
    ))}
  </div>
);

const GenericExample = ({ example }) => (
  <div className="space-y-3">
    <h4 className="font-semibold text-slate-900 dark:text-white">{example.title}</h4>
    <p className="text-sm text-slate-600 dark:text-slate-400">{example.description}</p>
  </div>
);

const FullTripExample = ({ example }) => (
  <div className="space-y-4">
    <div className="p-4 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 rounded-xl">
      <h4 className="font-bold text-lg text-slate-900 dark:text-white mb-2">{example.title}</h4>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-orange-500" />
          <span className="text-slate-700 dark:text-slate-300">{example.trip.destination}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-orange-500" />
          <span className="text-slate-700 dark:text-slate-300">{example.trip.dates}</span>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-orange-500" />
          <span className="text-slate-700 dark:text-slate-300">{example.trip.members} pessoas</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-orange-500" />
          <span className="text-slate-700 dark:text-slate-300">{example.trip.budget}</span>
        </div>
      </div>
    </div>

    {example.sections.map((section, i) => (
      <div key={i} className="space-y-2">
        <h5 className="font-semibold text-slate-900 dark:text-white text-sm">{section.title}</h5>
        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
          <ul className="space-y-1">
            {section.content.map((item, j) => (
              <li key={j} className="text-xs text-slate-600 dark:text-slate-400 flex items-start gap-2">
                <ChevronRight className="w-3 h-3 mt-0.5 text-orange-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    ))}
  </div>
);

export default ScreenHelp;
